import React, { useEffect, useState } from "react";
import {
  Button,
  EmptyModal,
  Textarea,
  Textfield,
} from "../../../../components/core";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";

function convertTimestampToCustomFormat(timestamp) {
  const stringifyTimepstamp = timestamp;
  const year = stringifyTimepstamp.slice(0, 4);
  const month = stringifyTimepstamp.slice(4, 6);
  const day = stringifyTimepstamp.slice(6, 8);
  const hour = parseInt(stringifyTimepstamp.slice(8, 10)) + 8;
  const minute = stringifyTimepstamp.slice(10, 12);
  const second = stringifyTimepstamp.slice(12, 14);

  // Define an array to map month numbers to month names
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Convert month to the corresponding month name
  const monthName = monthNames[parseInt(month, 10) - 1];

  // Convert 24-hour format to 12-hour format with "am" or "pm"
  let ampm = "am";
  let hour12 = hour;
  if (hour12 >= 12) {
    ampm = "pm";
    if (hour12 > 12) {
      hour12 -= 12;
    }
  }

  // Construct the formatted date string
  const formattedDate = `${monthName} ${day}, ${year} ${hour12}:${minute}${ampm}`;

  return formattedDate;
}

export default function ViewSurveillancePhotosModal({
  open = false,
  onClose,
  // to test image grouping, you can get the logged array from operator admin live env console
  images = [],
  waybillNumber = "",
  location = "",
  sendEmailForSurveillance,
  setForm,
  form,
  disabled = false,
  packageState,
  packageId,
}) {
  const [dropOffPhotos, setDropOffPhotos] = useState([]);
  const [pickupPhotos, setPickupPhotos] = useState([]);
  // New state for grouped photos
  const [dropOffGroupedByCamera, setDropOffGroupedByCamera] = useState({});
  const [pickupGroupedByCamera, setPickupGroupedByCamera] = useState({});

  useEffect(() => {
    if (images.length > 0) {
      processSnapshotsEnhanced(images);
    }
  }, [images]);

  console.log({ images });

  // Enhanced logic for grouping photos by dropoff/pickup and camera type
  const processSnapshotsEnhanced = (snapshots) => {
    if (!snapshots || snapshots.length === 0) {
      setDropOffPhotos([]);
      setPickupPhotos([]);
      setDropOffGroupedByCamera({});
      setPickupGroupedByCamera({});
      return;
    }

    // Sort snapshots by timestamp
    const sortedSnapshots = [...snapshots].sort(
      (a, b) => parseInt(a.timestamp) - parseInt(b.timestamp)
    );

    console.log("Analyzing snapshots for dropoff/pickup separation...");

    // Strategy 1: Look for natural clusters in the timeline
    const events = detectPhotoEvents(sortedSnapshots);

    if (events.length >= 2) {
      // Multiple events detected - use first as dropoff, last as pickup
      const dropOff = events[0].photos;
      const pickup = events[events.length - 1].photos;

      console.log("Multiple events detected:", {
        totalEvents: events.length,
        dropOffCount: dropOff.length,
        pickupCount: pickup.length,
      });

      setDropOffPhotos(dropOff);
      setPickupPhotos(pickup);
      setDropOffGroupedByCamera(organizePhotosByCamera(dropOff));
      setPickupGroupedByCamera(organizePhotosByCamera(pickup));
      return;
    }

    // Strategy 2: If single event but many photos, try camera-based separation
    if (sortedSnapshots.length > 6) {
      const cameraBasedSplit = separateByCamera(sortedSnapshots);
      if (cameraBasedSplit.canSeparate) {
        setDropOffPhotos(cameraBasedSplit.dropOff);
        setPickupPhotos(cameraBasedSplit.pickup);
        setDropOffGroupedByCamera(
          organizePhotosByCamera(cameraBasedSplit.dropOff)
        );
        setPickupGroupedByCamera(
          organizePhotosByCamera(cameraBasedSplit.pickup)
        );
        return;
      }
    }

    // Strategy 3: Time-based separation with dynamic thresholds
    const timeSplit = separateByTime(sortedSnapshots);
    if (timeSplit.canSeparate) {
      setDropOffPhotos(timeSplit.dropOff);
      setPickupPhotos(timeSplit.pickup);
      setDropOffGroupedByCamera(organizePhotosByCamera(timeSplit.dropOff));
      setPickupGroupedByCamera(organizePhotosByCamera(timeSplit.pickup));
      return;
    }

    // Default: treat as single dropoff event
    console.log("No clear separation found - treating as single dropoff event");
    setDropOffPhotos(sortedSnapshots);
    setPickupPhotos([]);
    setDropOffGroupedByCamera(organizePhotosByCamera(sortedSnapshots));
    setPickupGroupedByCamera({});
  };

  // Strategy 1: Detect photo events by clustering nearby timestamps
  const detectPhotoEvents = (sortedSnapshots) => {
    if (sortedSnapshots.length <= 1) return [{ photos: sortedSnapshots }];

    const events = [];
    let currentEvent = [sortedSnapshots[0]];

    // Dynamic threshold based on total time span
    const totalTimeSpan =
      parseInt(sortedSnapshots[sortedSnapshots.length - 1].timestamp) -
      parseInt(sortedSnapshots[0].timestamp);

    // Adaptive threshold: shorter for quick sessions, longer for extended periods
    let gapThreshold;
    if (totalTimeSpan < 1000) {
      // Less than ~10 minutes total
      gapThreshold = 200; // 2 minutes gap
    } else if (totalTimeSpan < 3000) {
      // Less than ~30 minutes total
      gapThreshold = 500; // 5 minutes gap
    } else {
      gapThreshold = 1000; // 10 minutes gap for longer sessions
    }

    for (let i = 1; i < sortedSnapshots.length; i++) {
      const currentTime = parseInt(sortedSnapshots[i].timestamp);
      const previousTime = parseInt(sortedSnapshots[i - 1].timestamp);
      const gap = currentTime - previousTime;

      if (gap > gapThreshold) {
        // Significant gap found - start new event
        events.push({ photos: currentEvent, gap: gap });
        currentEvent = [sortedSnapshots[i]];
      } else {
        currentEvent.push(sortedSnapshots[i]);
      }
    }

    // Add the last event
    events.push({ photos: currentEvent });

    console.log("Event detection results:", {
      totalTimeSpan,
      gapThreshold,
      eventsFound: events.length,
      eventSizes: events.map((e) => e.photos.length),
    });

    return events;
  };

  // Strategy 2: Separate by camera usage patterns
  const separateByCamera = (sortedSnapshots) => {
    // Group consecutive photos by camera type
    const cameraSequences = [];
    let currentSequence = {
      camera: sortedSnapshots[0].camera_label,
      photos: [sortedSnapshots[0]],
      startTime: parseInt(sortedSnapshots[0].timestamp),
    };

    for (let i = 1; i < sortedSnapshots.length; i++) {
      const photo = sortedSnapshots[i];
      if (photo.camera_label === currentSequence.camera) {
        currentSequence.photos.push(photo);
      } else {
        // Camera changed - finish current sequence
        currentSequence.endTime = parseInt(sortedSnapshots[i - 1].timestamp);
        cameraSequences.push(currentSequence);

        currentSequence = {
          camera: photo.camera_label,
          photos: [photo],
          startTime: parseInt(photo.timestamp),
        };
      }
    }

    // Finish last sequence
    currentSequence.endTime = parseInt(
      sortedSnapshots[sortedSnapshots.length - 1].timestamp
    );
    cameraSequences.push(currentSequence);

    console.log(
      "Camera sequence analysis:",
      cameraSequences.map((seq) => ({
        camera: seq.camera,
        photoCount: seq.photos.length,
        duration: seq.endTime - seq.startTime,
      }))
    );

    // Look for pattern: if we have clear camera transitions, split there
    if (cameraSequences.length >= 3) {
      // Find the midpoint sequence to split on
      const midIndex = Math.floor(cameraSequences.length / 2);
      const dropOffSequences = cameraSequences.slice(0, midIndex);
      const pickupSequences = cameraSequences.slice(midIndex);

      return {
        canSeparate: true,
        dropOff: dropOffSequences.flatMap((seq) => seq.photos),
        pickup: pickupSequences.flatMap((seq) => seq.photos),
      };
    }

    return { canSeparate: false };
  };

  // Strategy 3: Enhanced time-based separation with dynamic thresholds
  const separateByTime = (sortedSnapshots) => {
    const timeGaps = [];

    for (let i = 1; i < sortedSnapshots.length; i++) {
      const gap =
        parseInt(sortedSnapshots[i].timestamp) -
        parseInt(sortedSnapshots[i - 1].timestamp);
      timeGaps.push({
        index: i,
        gap: gap,
        beforePhoto: sortedSnapshots[i - 1],
        afterPhoto: sortedSnapshots[i],
      });
    }

    // Sort gaps by size to find the largest natural break
    const sortedGaps = [...timeGaps].sort((a, b) => b.gap - a.gap);

    // Use the largest gap if it's significant relative to other gaps
    if (sortedGaps.length > 0) {
      const largestGap = sortedGaps[0];
      const secondLargestGap = sortedGaps[1] || { gap: 0 };

      // Only split if the gap is at least 3x larger than the second largest
      // and represents a meaningful pause (at least 3 minutes)
      if (largestGap.gap > 300 && largestGap.gap > secondLargestGap.gap * 3) {
        const dropOff = sortedSnapshots.slice(0, largestGap.index);
        const pickup = sortedSnapshots.slice(largestGap.index);

        console.log("Time-based split found:", {
          gapSize: largestGap.gap,
          gapMinutes: Math.round(largestGap.gap / 100),
          dropOffCount: dropOff.length,
          pickupCount: pickup.length,
        });

        return {
          canSeparate: true,
          dropOff: dropOff,
          pickup: pickup,
        };
      }
    }

    return { canSeparate: false };
  };

  // Enhanced camera organization with validation
  const organizePhotosByCamera = (photos) => {
    const grouped = {};
    const cameraTypes = ["c1", "c2", "fc"]; // Define expected camera types

    photos.forEach((photo) => {
      let cameraKey = photo.camera_label || "other";

      // Normalize camera labels (handle variations like 'C1', 'Camera1', etc.)
      const normalizedCamera = normalizeCameraLabel(cameraKey);
      if (normalizedCamera) {
        cameraKey = normalizedCamera;
      }

      if (!grouped[cameraKey]) {
        grouped[cameraKey] = [];
      }

      grouped[cameraKey].push({
        ...photo,
        image: photo.image,
      });
    });

    // Ensure consistent ordering of camera groups
    const orderedGrouped = {};
    cameraTypes.forEach((cameraType) => {
      if (grouped[cameraType]) {
        orderedGrouped[cameraType] = grouped[cameraType];
      }
    });

    // Add any other cameras at the end
    Object.keys(grouped).forEach((key) => {
      if (!cameraTypes.includes(key)) {
        orderedGrouped[key] = grouped[key];
      }
    });

    console.log(
      "Camera grouping results:",
      Object.keys(orderedGrouped).map((camera) => ({
        camera,
        photoCount: orderedGrouped[camera].length,
      }))
    );

    return orderedGrouped;
  };

  // Helper function to normalize camera labels
  const normalizeCameraLabel = (label) => {
    if (!label) return null;

    const normalized = label.toLowerCase().trim();

    // Map various camera label formats to standard format
    const cameraMap = {
      c1: "c1",
      camera1: "c1",
      cam1: "c1",
      c2: "c2",
      camera2: "c2",
      cam2: "c2",
      fc: "fc",
      front: "fc",
      frontcam: "fc",
      front_camera: "fc",
    };

    return cameraMap[normalized] || null;
  };

  // Additional utility: Analyze photo distribution by camera and time
  const analyzePhotoDistribution = (snapshots) => {
    const analysis = {
      totalPhotos: snapshots.length,
      cameraDistribution: {},
      timeSpan: 0,
      averageInterval: 0,
    };

    if (snapshots.length === 0) return analysis;

    // Camera distribution
    snapshots.forEach((photo) => {
      const camera = photo.camera_label || "unknown";
      analysis.cameraDistribution[camera] =
        (analysis.cameraDistribution[camera] || 0) + 1;
    });

    // Time analysis
    const sortedSnapshots = [...snapshots].sort(
      (a, b) => parseInt(a.timestamp) - parseInt(b.timestamp)
    );
    if (sortedSnapshots.length > 1) {
      analysis.timeSpan =
        parseInt(sortedSnapshots[sortedSnapshots.length - 1].timestamp) -
        parseInt(sortedSnapshots[0].timestamp);
      analysis.averageInterval =
        analysis.timeSpan / (sortedSnapshots.length - 1);
    }

    console.log("Photo distribution analysis:", analysis);
    return analysis;
  };

  // Helper function to get camera display name
  const getCameraDisplayName = (cameraType) => {
    switch (cameraType) {
      case "cctv1":
      case "c1":
      case "camera 1":
        return "CCTV 1";

      case "cctv2":
      case "c2":
      case "camera 2":
        return "CCTV 2";

      case "facecam":
      case "face":
      case "fc":
        return "Face Camera";

      case "other":
        return "Other Cameras";

      default:
        return cameraType;
    }
  };

  return (
    <>
      <Dialog
        visible={open}
        onHide={onClose}
        header="Surveillance Photos"
        className="max-w-[95%] md:w-[60%]"
        draggable={false}
        resizable={false}
        pt={{
          content: { className: "overflow-y-auto md:overflow-hidden" },
        }}
      >
        <div className="grid grid-cols-3 w-full gap-5 h-full md:h-[70vh]">
          {/* LEFT SIDE - Scrollable Images */}
          <div className="col-span-3 md:col-span-2 overflow-y-auto pr-4">
            <div className="">
              <div className="font-semibold text-black text-[19px] pb-2">
                Package Dropoff
              </div>
              {/* Group photos by camera_label */}
              {Object.keys(dropOffGroupedByCamera).map((cameraType) => {
                // Skip empty camera groups
                if (dropOffGroupedByCamera[cameraType].length === 0)
                  return null;

                return (
                  <div key={`dropoff-${cameraType}`} className="mb-6">
                    <div className="font-medium text-black text-[16px] mb-2">
                      {getCameraDisplayName(cameraType)}
                    </div>
                    <div className="space-y-4">
                      {dropOffGroupedByCamera[cameraType].map((data, key) => (
                        <div key={`${cameraType}-${key}`}>
                          <div className="text-sm mb-1">
                            Timestamp:{" "}
                            {convertTimestampToCustomFormat(data.timestamp)}
                          </div>

                          <img
                            style={{ maxHeight: "400px" }}
                            src={data.image}
                            className="w-full border-b-2 border-black"
                            alt={`${cameraType} dropoff view ${key + 1}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <Divider className="border border-gray-200 px-11"></Divider>

            <div>
              <div className="font-semibold text-black text-[19px] pb-2">
                Package Pickup
              </div>
              {/* Group photos by camera_label */}
              {Object.keys(pickupGroupedByCamera).map((cameraType) => {
                // Skip empty camera groups
                if (pickupGroupedByCamera[cameraType].length === 0) return null;

                return (
                  <div key={`pickup-${cameraType}`} className="mb-6">
                    <div className="font-medium text-gray-700 text-[16px] mb-2">
                      {getCameraDisplayName(cameraType)}
                    </div>
                    <div className="space-y-4">
                      {pickupGroupedByCamera[cameraType].map((data, key) => (
                        <div key={`${cameraType}-${key}`}>
                          <div className="text-sm mb-1">
                            Timestamp:{" "}
                            {convertTimestampToCustomFormat(data.timestamp)}
                          </div>

                          <img
                            style={{ maxHeight: "400px" }}
                            src={data.image}
                            className="w-full border-b-2 border-black"
                            alt={`${cameraType} pickup view ${key + 1}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT SIDE - Fixed Package Details */}
          <div className="col-span-3 md:col-span-1 md:pt-0 pt-6">
            <div>
              <div className="col-span-2 text-[19px] text-black font-bold pb-4">
                Package Details
              </div>
              {packageState > 300 ? (
                <div className="grid grid-cols-4 w-full items-center">
                  <div className="col-span-2">Package ID:</div>
                  <div className="col-span-2">{packageId}</div>
                </div>
              ) : (
                <div className="grid grid-cols-4 w-full items-center">
                  <div className="col-span-2">Waybill Number:</div>
                  <div className="col-span-2">{waybillNumber}</div>
                </div>
              )}
              <div className="grid grid-cols-4 w-full items-center">
                <div className="col-span-2">Locker Name:</div>
                <div className="col-span-2">{location}</div>
              </div>
              <Divider className="border border-gray-200 px-11"></Divider>
              <form onSubmit={sendEmailForSurveillance}>
                <div className="col-span-1 my-4">
                  <div className="mb-4">
                    <p className="my-auto font-bold">Send to: </p>
                    <Textfield
                      disabled={disabled}
                      required={true}
                      type={"email"}
                      placeholder={"Type email"}
                      maxLength={32}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, email: e.target.value }))
                      }
                    />
                  </div>
                  <div className="mb-4">
                    <p className="my-auto font-bold">Subject: </p>
                    <p className="my-auto">
                      Surveillance Photos for Waybill <b>{waybillNumber}</b>
                    </p>
                  </div>
                  <Textarea
                    disabled={disabled}
                    placeholder="Type message here"
                    maxLength={1000}
                    value={form.description}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                  />
                  <Button
                    className={"p-2 w-full"}
                    value={"Send"}
                    disabled={disabled}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
